import { toast } from 'sonner';
import type { TimelineItem, TimelineTrack } from '@/types/timeline';
import type { Transition } from '@/types/transition';
import type { ItemKeyframes } from '@/types/keyframe';
import { useSelectionStore } from '@/features/editor/stores/selection-store';
import { useItemsStore } from '@/features/timeline/stores/items-store';
import { useTransitionsStore } from '@/features/timeline/stores/transitions-store';
import { useKeyframesStore } from '@/features/timeline/stores/keyframes-store';
import { useCompositionsStore } from '@/features/timeline/stores/compositions-store';
import { useProjectStore } from '@/features/projects/stores/project-store';

type SceneSnapshot = {
  name: string;
  startFrame: number;
  endFrame: number;
  durationInFrames: number;
  tracks: TimelineTrack[];
  items: TimelineItem[];
  transitions: Transition[];
  keyframes: ItemKeyframes[];
};

const cloneTrack = (track: TimelineTrack): TimelineTrack => ({
  ...track,
  items: [],
});

const getAspectRatio = (width?: number, height?: number) => {
  if (!width || !height) return '16:9';
  if (width === height) return '1:1';
  return width < height ? '9:16' : '16:9';
};

const cleanText = (value?: string) => (value || '').replace(/\s+/g, ' ').trim();

const deriveTextFromItems = (items: TimelineItem[]) => {
  const allTextItems = items
    .filter((item) => item.type === 'text' && typeof item.text === 'string' && item.text.trim().length > 0);

  const captionFirstItems = allTextItems.filter((item) =>
    item.trackId?.toLowerCase().includes('caption') ||
    item.label?.toLowerCase().includes('caption') ||
    (item as any).role === 'caption'
  );

  const sourceItems = (captionFirstItems.length > 0 ? captionFirstItems : allTextItems)
    .sort((a, b) => {
      if (a.from !== b.from) return a.from - b.from;
      return a.durationInFrames - b.durationInFrames;
    });

  const dedupedTexts: string[] = [];
  for (const item of sourceItems) {
    const nextText = cleanText(item.text);
    if (!nextText) continue;
    if (dedupedTexts[dedupedTexts.length - 1] === nextText) continue;
    dedupedTexts.push(nextText);
  }

  return cleanText(dedupedTexts.join(' '));
};

const derivePrimaryVisual = (items: TimelineItem[]) => {
  const visualItems = items.filter((item) => item.type === 'video' || item.type === 'image');
  return visualItems.sort((a, b) => a.from - b.from)[0];
};

const derivePrimaryAudio = (items: TimelineItem[]) => {
  const audioItems = items.filter((item) => item.type === 'audio' && typeof item.src === 'string');
  return audioItems.sort((a, b) => a.from - b.from)[0];
};

const overlapsRange = (item: TimelineItem, startFrame: number, endFrame: number) => {
  const itemEnd = item.from + item.durationInFrames;
  return item.from < endFrame && itemEnd > startFrame;
};

const normalizeItemToRange = (item: TimelineItem, startFrame: number, endFrame: number): TimelineItem => {
  const itemStart = item.from;
  const itemEnd = item.from + item.durationInFrames;
  const clippedStart = Math.max(itemStart, startFrame);
  const clippedEnd = Math.min(itemEnd, endFrame);
  const startOffset = clippedStart - itemStart;
  const nextDuration = Math.max(1, clippedEnd - clippedStart);

  const nextItem: TimelineItem = {
    ...item,
    from: clippedStart - startFrame,
    durationInFrames: nextDuration,
    transform: item.transform ? { ...item.transform } : item.transform,
  };

  if ('sourceStart' in nextItem || 'sourceEnd' in nextItem || 'trimStart' in nextItem) {
    const currentSourceStart = nextItem.sourceStart ?? 0;
    nextItem.sourceStart = currentSourceStart + startOffset;
    if (typeof nextItem.sourceEnd === 'number' && nextItem.sourceEnd > nextItem.sourceStart) {
      nextItem.sourceEnd = Math.min(nextItem.sourceEnd, nextItem.sourceStart + nextDuration);
    } else {
      nextItem.sourceEnd = nextItem.sourceStart + nextDuration;
    }
  }

  return nextItem;
};

const extractRootSelectionSnapshot = (
  selectedIds: string[],
  items: TimelineItem[],
  tracks: TimelineTrack[],
  transitions: Transition[],
  keyframes: ItemKeyframes[]
): SceneSnapshot | null => {
  const selectedItems = items.filter((item) => selectedIds.includes(item.id));
  if (selectedItems.length === 0) return null;

  const primarySceneItem =
    selectedItems.find((item) => item.label?.startsWith('Scene')) ||
    selectedItems.find((item) => item.type === 'video' || item.type === 'image') ||
    selectedItems.find((item) => item.type === 'composition');

  const startFrame = primarySceneItem
    ? primarySceneItem.from
    : Math.min(...selectedItems.map((item) => item.from));
  const endFrame = primarySceneItem
    ? primarySceneItem.from + primarySceneItem.durationInFrames
    : Math.max(...selectedItems.map((item) => item.from + item.durationInFrames));

  const relatedItems = items.filter((item) => overlapsRange(item, startFrame, endFrame));
  const trackIdSet = new Set(relatedItems.map((item) => item.trackId));
  const snapshotTracks = tracks
    .filter((track) => trackIdSet.has(track.id))
    .sort((a, b) => a.order - b.order)
    .map(cloneTrack);

  const relatedItemIds = new Set(relatedItems.map((item) => item.id));
  const snapshotTransitions = transitions
    .filter((transition) => relatedItemIds.has(transition.leftClipId) && relatedItemIds.has(transition.rightClipId))
    .map((transition) => ({ ...transition }));

  const snapshotKeyframes = keyframes
    .filter((entry) => relatedItemIds.has(entry.itemId))
    .map((entry) => ({
      ...entry,
      properties: entry.properties.map((property) => ({
        ...property,
        keyframes: property.keyframes.map((keyframe) => ({ ...keyframe })),
      })),
    }));

  return {
    name: selectedItems.find((item) => item.label)?.label || 'Saved Scene',
    startFrame,
    endFrame,
    durationInFrames: endFrame - startFrame,
    tracks: snapshotTracks,
    items: relatedItems.map((item) => normalizeItemToRange(item, startFrame, endFrame)),
    transitions: snapshotTransitions,
    keyframes: snapshotKeyframes,
  };
};

const extractCompositionSnapshot = (selectedItemId: string): SceneSnapshot | null => {
  const items = useItemsStore.getState().items;
  const compositionItem = items.find((item) => item.id === selectedItemId && item.type === 'composition');
  if (!compositionItem || compositionItem.type !== 'composition') return null;

  const composition = useCompositionsStore.getState().getComposition(compositionItem.compositionId);
  if (!composition) return null;

  return {
    name: compositionItem.label || composition.name || 'Saved Scene',
    startFrame: compositionItem.from,
    endFrame: compositionItem.from + composition.durationInFrames,
    durationInFrames: composition.durationInFrames,
    tracks: composition.tracks.map(cloneTrack),
    items: composition.items.map((item) => ({
      ...item,
      transform: item.transform ? { ...item.transform } : item.transform,
    })),
    transitions: composition.transitions.map((transition) => ({ ...transition })),
    keyframes: composition.keyframes.map((entry) => ({
      ...entry,
      properties: entry.properties.map((property) => ({
        ...property,
        keyframes: property.keyframes.map((keyframe) => ({ ...keyframe })),
      })),
    })),
  };
};

export const saveSelectionAsSceneAsset = () => {
  const selectedIds = useSelectionStore.getState().selectedItemIds;
  if (selectedIds.length === 0) {
    toast.error('먼저 저장할 씬 범위를 선택하세요.');
    return false;
  }

  const currentProject = useProjectStore.getState().currentProject;
  if (!currentProject) {
    toast.error('프로젝트 정보를 찾지 못했습니다.');
    return false;
  }

  const itemsState = useItemsStore.getState();
  const transitionsState = useTransitionsStore.getState();
  const keyframesState = useKeyframesStore.getState();
  const projectInput = currentProject.input_data || {};
  const fps = currentProject.metadata?.fps || 30;
  const width = currentProject.metadata?.width || 1920;
  const height = currentProject.metadata?.height || 1080;
  const aspectRatio = getAspectRatio(width, height);

  const snapshot =
    (selectedIds.length === 1 ? extractCompositionSnapshot(selectedIds[0]) : null) ||
    extractRootSelectionSnapshot(
      selectedIds,
      itemsState.items,
      itemsState.tracks,
      transitionsState.transitions,
      keyframesState.keyframes
    );

  if (!snapshot || snapshot.items.length === 0) {
    toast.error('선택한 씬을 저장할 수 없습니다.');
    return false;
  }

  const derivedText = deriveTextFromItems(snapshot.items);
  const primaryVisual = derivePrimaryVisual(snapshot.items);
  const primaryAudio = derivePrimaryAudio(snapshot.items);
  const suggestedName = cleanText(snapshot.name) || 'Saved Scene';

  window.parent.postMessage(
    {
      type: 'SAVE_SCENE_ASSET',
      payload: {
        projectId: currentProject.id,
        sceneAsset: {
          name: suggestedName,
          description: derivedText || `superedit에서 저장한 씬 자산 - ${suggestedName}`,
          sceneKind: 'general',
          aspectRatio,
          payload: {
            scene: {
              title: suggestedName,
              description: derivedText,
              prompt: cleanText(projectInput?.scriptPrompt || ''),
              image: primaryVisual?.type === 'image' ? primaryVisual.src : '',
              video: primaryVisual?.type === 'video' ? primaryVisual.src : '',
              duration: Math.max(1, Math.round((snapshot.durationInFrames / fps) * 10) / 10),
              generatedAudioUrl: primaryAudio?.src || '',
            },
            selectedVoiceId: projectInput?.selectedVoiceId || 'auto',
            audioConfig: projectInput?.audioConfig,
            subtitleSettings: projectInput?.subtitleSettings,
            templateTimeline: {
              source: 'superedit',
              layoutLocked: true,
              startFrame: snapshot.startFrame,
              endFrame: snapshot.endFrame,
              timeline: {
                tracks: snapshot.tracks,
                items: snapshot.items,
                transitions: snapshot.transitions,
                keyframes: snapshot.keyframes,
                width,
                height,
                fps,
                durationInFrames: snapshot.durationInFrames,
              },
            },
            editableFields: {
              text: true,
              voice: true,
              media: true,
              subtitles: true,
            },
          },
        },
      },
      data: {
        projectId: currentProject.id,
        sceneAsset: {
          name: suggestedName,
        },
      },
    },
    '*'
  );

  toast.success('씬 저장 요청을 보냈습니다.');
  return true;
};
